import { useState, useEffect, useRef, useCallback } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { Play, Pause, RotateCcw, AlertTriangle, Shield, Crosshair } from 'lucide-react';
import SimulationScenario2 from './SimulationScenario2';
import SimulationScenario3 from './SimulationScenario3';
import SimulationScenario4 from './SimulationScenario4';
import SimulationScenario5 from './SimulationScenario5';

function ScenarioTabs({ active, onSwitch }) {
  const tabs = [
    { id: 1, label: 'Scenario 1', activeColor: 'bg-sky-500/20 text-sky-400 border-sky-500/50' },
    { id: 2, label: 'Scenario 2', activeColor: 'bg-red-500/20 text-red-400 border-red-500/50' },
    { id: 3, label: 'Scenario 3', activeColor: 'bg-amber-500/20 text-amber-400 border-amber-500/50' },
    { id: 4, label: 'Scenario 4', activeColor: 'bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-500/50' },
    { id: 5, label: 'Scenario 5', activeColor: 'bg-purple-500/20 text-purple-300 border-purple-500/50' },
  ];
  return (
    <div className="flex items-center gap-2">
      {tabs.map(t => (
        <button key={t.id}
          onClick={() => onSwitch(t.id)}
          className={`px-4 py-2 rounded font-mono text-xs uppercase tracking-wider border transition-all ${
            active === t.id ? t.activeColor : 'bg-zinc-800 text-zinc-400 border-zinc-700 hover:border-zinc-500'
          }`}
          data-testid={`tab-scenario${t.id}`}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

export default function Simulation() {
  const [activeScenario, setActiveScenario] = useState(1);

  if (activeScenario >= 2 && activeScenario <= 5) {
    const Comp = activeScenario === 2 ? SimulationScenario2
              : activeScenario === 3 ? SimulationScenario3
              : activeScenario === 4 ? SimulationScenario4
              : SimulationScenario5;
    return (
      <div className="h-full flex flex-col bg-zinc-950" data-testid="simulation-page">
        <div className="bg-zinc-900/50 border-b border-zinc-800 p-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold tracking-tight uppercase flex items-center gap-2">
                <Crosshair size={24} className="text-sky-500" />
                Threat Simulation
              </h1>
              <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1 font-mono">
                Counter-Drone Defense Demonstration
              </p>
            </div>
            <ScenarioTabs active={activeScenario} onSwitch={setActiveScenario} />
          </div>
        </div>
        <div className="flex-1 overflow-hidden">
          <Comp />
        </div>
      </div>
    );
  }

  return <Scenario1View onSwitchScenario={setActiveScenario} />;
}

function Scenario1View({ onSwitchScenario }) {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const animationRef = useRef(null);
  const threatsRef = useRef([]);
  const isPlayingRef = useRef(false);
  const phaseRef = useRef('ready');
  const addLogRef = useRef(null);
  const setThreatStatusRef = useRef(null);
  const setSimulationPhaseRef = useRef(null);
  const setIsPlayingRef = useRef(null);
  
  const [isPlaying, setIsPlaying] = useState(false);
  const [simulationPhase, setSimulationPhase] = useState('ready');
  const [logs, setLogs] = useState([]);
  const [threatStatus, setThreatStatus] = useState({
    air1: 'approaching',
    air2: 'approaching',
    surface: 'approaching',
    underwater: 'approaching'
  });

  // Store refs to state setters for use in animation loop
  useEffect(() => {
    setThreatStatusRef.current = setThreatStatus;
    setSimulationPhaseRef.current = setSimulationPhase;
    setIsPlayingRef.current = setIsPlaying;
  }, []);

  // Sync playing state with ref
  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  useEffect(() => {
    phaseRef.current = simulationPhase;
  }, [simulationPhase]);

  const addLog = useCallback((message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev.slice(-15), { timestamp, message, type }]);
  }, []);

  useEffect(() => {
    addLogRef.current = addLog;
  }, [addLog]);

  // Initialize Three.js scene
  useEffect(() => {
    if (!containerRef.current) return;

    // Scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x020617);
    scene.fog = new THREE.FogExp2(0x020617, 0.008);
    sceneRef.current = scene;

    // Camera
    const camera = new THREE.PerspectiveCamera(
      55,
      containerRef.current.clientWidth / containerRef.current.clientHeight,
      0.1,
      1000
    );
    camera.position.set(35, 25, 35);
    camera.lookAt(0, 0, 0);

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    containerRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.minDistance = 20;
    controls.maxDistance = 100;

    // Lighting
    scene.add(new THREE.AmbientLight(0xffffff, 0.4));
    const dirLight = new THREE.DirectionalLight(0xffffff, 0.8);
    dirLight.position.set(10, 20, 10);
    scene.add(dirLight);
    scene.add(new THREE.PointLight(0x0ea5e9, 0.5, 100));

    // Ocean
    const ocean = new THREE.Mesh(
      new THREE.PlaneGeometry(200, 200),
      new THREE.MeshStandardMaterial({ color: 0x0c4a6e, transparent: true, opacity: 0.4, side: THREE.DoubleSide })
    );
    ocean.rotation.x = -Math.PI / 2;
    ocean.position.y = -1;
    scene.add(ocean);

    // Ship
    createShip(scene);

    // Protection spheres
    createProtectionSphere(scene, 15, 0x0ea5e9, 0.08);
    createProtectionSphere(scene, 6, 0x06b6d4, 0.1);
    createProtectionSphere(scene, 3, 0x14b8a6, 0.12);

    // Create threats
    initThreats(scene);

    // Animation loop
    const animate = () => {
      animationRef.current = requestAnimationFrame(animate);
      
      // Rotate protection spheres
      scene.children.forEach((child) => {
        if (child.userData.isProtectionSphere) {
          child.rotation.y += 0.001;
        }
      });

      // Move threats if playing
      if (isPlayingRef.current) {
        updateThreatsInScene(scene);
      }

      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    // Resize handler
    const handleResize = () => {
      if (!containerRef.current) return;
      camera.aspect = containerRef.current.clientWidth / containerRef.current.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationRef.current);
      if (containerRef.current && renderer.domElement) {
        containerRef.current.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, []);

  const createShip = (scene) => {
    const group = new THREE.Group();
    const scale = 0.5;

    const hullMat = new THREE.MeshStandardMaterial({ color: 0xfbbf24, metalness: 0.5, roughness: 0.3 });
    const hull = new THREE.Mesh(new THREE.BoxGeometry(2 * scale, 1 * scale, 6 * scale), hullMat);
    hull.position.y = -0.3 * scale;
    group.add(hull);

    const deck = new THREE.Mesh(
      new THREE.BoxGeometry(1.8 * scale, 0.2 * scale, 5.5 * scale),
      new THREE.MeshStandardMaterial({ color: 0xf59e0b })
    );
    deck.position.y = 0.2 * scale;
    group.add(deck);

    const bridge = new THREE.Mesh(
      new THREE.BoxGeometry(1.2 * scale, 1 * scale, 1.5 * scale),
      new THREE.MeshStandardMaterial({ color: 0xeab308 })
    );
    bridge.position.set(0, 0.8 * scale, -0.8 * scale);
    group.add(bridge);

    const mast = new THREE.Mesh(
      new THREE.CylinderGeometry(0.04 * scale, 0.04 * scale, 1 * scale, 8),
      new THREE.MeshStandardMaterial({ color: 0x333333 })
    );
    mast.position.set(0, 1.5 * scale, -0.8 * scale);
    group.add(mast);

    const dome = new THREE.Mesh(
      new THREE.SphereGeometry(0.2 * scale, 16, 16),
      new THREE.MeshStandardMaterial({ color: 0x0ea5e9, emissive: 0x0ea5e9, emissiveIntensity: 0.5 })
    );
    dome.position.set(0, 2.1 * scale, -0.8 * scale);
    group.add(dome);

    scene.add(group);
  };

  const createProtectionSphere = (scene, radius, color, opacity) => {
    const sphere = new THREE.Mesh(
      new THREE.SphereGeometry(radius, 32, 32),
      new THREE.MeshBasicMaterial({ color, transparent: true, opacity, wireframe: true, side: THREE.DoubleSide })
    );
    sphere.userData.isProtectionSphere = true;
    scene.add(sphere);
  };

  const initThreats = (scene) => {
    threatsRef.current = [];

    const threats = [
      { id: 'air1', type: 'air', color: 0xef4444, startPos: [-20, 10, -18], speed: 0.15 },
      { id: 'air2', type: 'air', color: 0xf59e0b, startPos: [18, 9, -20], speed: 0.14 },
      { id: 'surface', type: 'surface', color: 0x22c55e, startPos: [22, 0.5, 12], speed: 0.12 },
      { id: 'underwater', type: 'underwater', color: 0x3b82f6, startPos: [-15, -4, 18], speed: 0.10 }
    ];

    threats.forEach(t => {
      const mesh = createThreatMesh(t.type, t.color);
      mesh.position.set(...t.startPos);
      mesh.userData = {
        id: t.id,
        type: t.type,
        speed: t.speed,
        status: 'approaching',
        startPos: new THREE.Vector3(...t.startPos),
        detected: false,
        tracking: false,
        engaged: false
      };
      scene.add(mesh);
      threatsRef.current.push(mesh);
    });
  };

  const createThreatMesh = (type, color) => {
    let geometry;
    if (type === 'air') {
      geometry = new THREE.OctahedronGeometry(0.8, 0);
    } else if (type === 'surface') {
      geometry = new THREE.BoxGeometry(1, 0.5, 0.8);
    } else {
      geometry = new THREE.SphereGeometry(0.6, 16, 16);
    }

    const mesh = new THREE.Mesh(
      geometry,
      new THREE.MeshStandardMaterial({ color, emissive: color, emissiveIntensity: 0.6 })
    );

    const ring = new THREE.Mesh(
      new THREE.RingGeometry(0.9, 1.0, 32),
      new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.7, side: THREE.DoubleSide })
    );
    ring.rotation.x = Math.PI / 2;
    mesh.add(ring);

    return mesh;
  };

  // Update threats - called from animation loop
  const updateThreatsInScene = (scene) => {
    const shipPos = new THREE.Vector3(0, 0, 0);

    threatsRef.current.forEach(threat => {
      // Handle completed threats
      if (threat.userData.status === 'neutralized') return;
      
      if (threat.userData.status === 'jammed' && threat.visible) {
        threat.position.y -= 0.1;
        threat.rotation.z += 0.08;
        threat.rotation.x += 0.05;
        if (threat.position.y < -2) {
          threat.visible = false;
        }
        return;
      }
      
      if (threat.userData.status === 'pushed' && threat.visible) {
        const pushDir = new THREE.Vector3().subVectors(threat.position, shipPos).normalize();
        threat.position.add(pushDir.multiplyScalar(0.2));
        const distFromShip = threat.position.distanceTo(shipPos);
        if (distFromShip > 22) {
          threat.visible = false;
          if (setThreatStatusRef.current) {
            setThreatStatusRef.current(prev => ({ ...prev, [threat.userData.id]: 'repelled' }));
          }
          if (addLogRef.current) {
            addLogRef.current(`[RESULT] UNDERWATER THREAT REPELLED - Pushed outside 5km perimeter`, 'success');
          }
        }
        return;
      }

      // Move threat towards ship
      const distance = threat.position.distanceTo(shipPos);
      const direction = new THREE.Vector3().subVectors(shipPos, threat.position).normalize();
      const targetY = threat.userData.type === 'air' ? 4 : threat.userData.type === 'underwater' ? -3 : 0.5;
      direction.y = (targetY - threat.position.y) * 0.03;
      threat.position.add(direction.multiplyScalar(threat.userData.speed));

      // Pulse effect
      const scale = 1 + Math.sin(Date.now() * 0.005) * 0.15;
      threat.scale.setScalar(scale);

      // Get threat label
      const threatLabel = threat.userData.id.toUpperCase()
        .replace('AIR1', 'AIR THREAT 1')
        .replace('AIR2', 'AIR THREAT 2')
        .replace('SURFACE', 'SURFACE THREAT')
        .replace('UNDERWATER', 'UNDERWATER THREAT');

      // PHASE 1: Detection at 5km (15 units)
      if (distance < 16 && !threat.userData.detected) {
        threat.userData.detected = true;
        if (phaseRef.current === 'ready' && setSimulationPhaseRef.current) {
          setSimulationPhaseRef.current('detecting');
        }
        if (addLogRef.current) {
          addLogRef.current(`[5KM LAYER] ${threatLabel} DETECTED - Entering defense perimeter`, 'warning');
        }
        if (setThreatStatusRef.current) {
          setThreatStatusRef.current(prev => ({ ...prev, [threat.userData.id]: 'detected' }));
        }
      }

      // PHASE 2: Tracking at 4km (12 units)
      if (distance < 13 && threat.userData.detected && !threat.userData.tracking) {
        threat.userData.tracking = true;
        if (addLogRef.current) {
          addLogRef.current(`[TRACKING] ${threatLabel} - Sensors locked, calculating intercept`, 'info');
        }
        if (setThreatStatusRef.current) {
          setThreatStatusRef.current(prev => ({ ...prev, [threat.userData.id]: 'tracking' }));
        }
      }

      // PHASE 3: Engagement at 2km (6 units)
      if (distance < 7 && !threat.userData.engaged) {
        threat.userData.engaged = true;
        if (setSimulationPhaseRef.current) {
          setSimulationPhaseRef.current('engaging');
        }

        if (threat.userData.id === 'air2') {
          // Electronic Jamming
          if (addLogRef.current) {
            addLogRef.current(`[2KM LAYER] ${threatLabel} - COUNTERMEASURE: Electronic Jamming`, 'danger');
          }
          
          for (let i = 0; i < 3; i++) {
            const wave = new THREE.Mesh(
              new THREE.RingGeometry(0.5 + i * 0.8, 0.7 + i * 0.8, 32),
              new THREE.MeshBasicMaterial({ color: 0xff00ff, transparent: true, opacity: 0.7, side: THREE.DoubleSide })
            );
            wave.position.copy(threat.position);
            wave.lookAt(shipPos);
            wave.userData.isEffect = true;
            wave.userData.life = 1.0;
            scene.add(wave);
          }

          setTimeout(() => {
            threat.userData.status = 'jammed';
            if (setThreatStatusRef.current) {
              setThreatStatusRef.current(prev => ({ ...prev, [threat.userData.id]: 'jammed' }));
            }
            if (addLogRef.current) {
              addLogRef.current(`[RESULT] ${threatLabel} JAMMED - Signal disrupted, drone falling`, 'success');
            }
          }, 500);

        } else if (threat.userData.id === 'underwater') {
          // Acoustic Waves
          if (addLogRef.current) {
            addLogRef.current(`[2KM LAYER] ${threatLabel} - COUNTERMEASURE: High Acoustic Waves`, 'danger');
          }

          for (let i = 0; i < 5; i++) {
            setTimeout(() => {
              const acousticWave = new THREE.Mesh(
                new THREE.RingGeometry(1, 1.5, 64),
                new THREE.MeshBasicMaterial({ color: 0x00ffff, transparent: true, opacity: 0.8, side: THREE.DoubleSide })
              );
              acousticWave.position.set(0, -3, 0);
              acousticWave.rotation.x = -Math.PI / 2;
              acousticWave.userData.isAcousticWave = true;
              acousticWave.userData.life = 1.0;
              scene.add(acousticWave);
            }, i * 150);
          }

          setTimeout(() => {
            threat.userData.status = 'pushed';
            if (setThreatStatusRef.current) {
              setThreatStatusRef.current(prev => ({ ...prev, [threat.userData.id]: 'repelling' }));
            }
            if (addLogRef.current) {
              addLogRef.current(`[RESULT] ${threatLabel} - Acoustic barrier pushing away`, 'warning');
            }
          }, 600);

        } else {
          // Laser Interception
          if (addLogRef.current) {
            addLogRef.current(`[2KM LAYER] ${threatLabel} - COUNTERMEASURE: Laser Interception`, 'danger');
          }

          const cmColor = threat.userData.type === 'air' ? 0x00ff00 : 0xffff00;
          const line = new THREE.Line(
            new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(0, 1, 0), threat.position.clone()]),
            new THREE.LineBasicMaterial({ color: cmColor, linewidth: 2 })
          );
          line.userData.isEffect = true;
          line.userData.life = 1.0;
          scene.add(line);

          const explosion = new THREE.Mesh(
            new THREE.SphereGeometry(1.2, 16, 16),
            new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.9 })
          );
          explosion.position.copy(threat.position);
          explosion.userData.isExplosion = true;
          explosion.userData.life = 1.0;
          scene.add(explosion);

          setTimeout(() => {
            threat.userData.status = 'neutralized';
            threat.visible = false;
            if (setThreatStatusRef.current) {
              setThreatStatusRef.current(prev => ({ ...prev, [threat.userData.id]: 'neutralized' }));
            }
            if (addLogRef.current) {
              addLogRef.current(`[RESULT] ${threatLabel} NEUTRALIZED - Target destroyed`, 'success');
            }
          }, 400);
        }
      }
    });

    // Clean up effects
    const toRemove = [];
    scene.children.forEach(child => {
      if (child.userData.isEffect || child.userData.isExplosion) {
        child.userData.life -= 0.03;
        if (child.material) child.material.opacity = child.userData.life;
        if (child.userData.isExplosion) child.scale.setScalar(1 + (1 - child.userData.life) * 3);
        if (child.userData.life <= 0) toRemove.push(child);
      }
      if (child.userData.isAcousticWave) {
        child.userData.life -= 0.02;
        child.material.opacity = child.userData.life;
        child.scale.setScalar(child.scale.x + 0.4);
        if (child.userData.life <= 0 || child.scale.x > 30) toRemove.push(child);
      }
    });
    toRemove.forEach(obj => scene.remove(obj));

    // Check completion
    const allDone = threatsRef.current.every(t => 
      t.userData.status === 'neutralized' || 
      (t.userData.status === 'jammed' && !t.visible) ||
      (t.userData.status === 'pushed' && !t.visible)
    );

    if (allDone && phaseRef.current !== 'neutralized') {
      if (setSimulationPhaseRef.current) {
        setSimulationPhaseRef.current('neutralized');
      }
      if (setIsPlayingRef.current) {
        setIsPlayingRef.current(false);
      }
      if (addLogRef.current) {
        addLogRef.current('════════════════════════════════════', 'success');
        addLogRef.current('ALL THREATS NEUTRALIZED - AREA SECURE', 'success');
        addLogRef.current('════════════════════════════════════', 'success');
      }
    }
  };

  const resetSimulation = () => {
    setIsPlaying(false);
    setSimulationPhase('ready');
    setLogs([]);
    setThreatStatus({ air1: 'approaching', air2: 'approaching', surface: 'approaching', underwater: 'approaching' });

    if (sceneRef.current) {
      const toRemove = [];
      sceneRef.current.children.forEach(child => {
        if (child.userData.isEffect || child.userData.isExplosion || child.userData.isAcousticWave) {
          toRemove.push(child);
        }
      });
      toRemove.forEach(obj => sceneRef.current.remove(obj));

      threatsRef.current.forEach(t => sceneRef.current.remove(t));
      initThreats(sceneRef.current);
    }
    addLog('Simulation reset - Ready to start', 'info');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approaching': return 'text-yellow-500';
      case 'detected': return 'text-orange-500';
      case 'tracking': return 'text-orange-400';
      case 'neutralized': return 'text-green-500';
      case 'jammed': return 'text-purple-500';
      case 'repelling': return 'text-cyan-500';
      case 'repelled': return 'text-cyan-400';
      default: return 'text-zinc-400';
    }
  };

  const getLogColor = (type) => {
    switch (type) {
      case 'warning': return 'text-yellow-400';
      case 'danger': return 'text-red-400';
      case 'success': return 'text-green-400';
      default: return 'text-zinc-400';
    }
  };

  return (
    <div className="h-full flex flex-col bg-zinc-950" data-testid="simulation-page">
      {/* Header */}
      <div className="bg-zinc-900/50 border-b border-zinc-800 p-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight uppercase flex items-center gap-2">
              <Crosshair size={24} className="text-sky-500" />
              Threat Simulation
            </h1>
            <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1 font-mono">
              Counter-Drone Defense Demonstration
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <button
                className="px-4 py-2 rounded font-mono text-xs uppercase tracking-wider bg-sky-500/20 text-sky-400 border border-sky-500/50"
                data-testid="tab-scenario1">
                Scenario 1
              </button>
              <button onClick={() => onSwitchScenario(2)}
                className="px-4 py-2 rounded font-mono text-xs uppercase tracking-wider bg-zinc-800 text-zinc-400 border border-zinc-700 hover:border-zinc-500 transition-all"
                data-testid="tab-scenario2">
                Scenario 2
              </button>
              <button onClick={() => onSwitchScenario(3)}
                className="px-4 py-2 rounded font-mono text-xs uppercase tracking-wider bg-zinc-800 text-zinc-400 border border-zinc-700 hover:border-amber-500/50 hover:text-amber-400 transition-all"
                data-testid="tab-scenario3">
                Scenario 3
              </button>
              <button onClick={() => onSwitchScenario(4)}
                className="px-4 py-2 rounded font-mono text-xs uppercase tracking-wider bg-zinc-800 text-zinc-400 border border-zinc-700 hover:border-fuchsia-500/50 hover:text-fuchsia-300 transition-all"
                data-testid="tab-scenario4">
                Scenario 4
              </button>
              <button onClick={() => onSwitchScenario(5)}
                className="px-4 py-2 rounded font-mono text-xs uppercase tracking-wider bg-zinc-800 text-zinc-400 border border-zinc-700 hover:border-purple-500/50 hover:text-purple-300 transition-all"
                data-testid="tab-scenario5">
                Scenario 5
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                disabled={simulationPhase === 'neutralized'}
                className={`flex items-center gap-2 px-4 py-2 rounded font-mono text-sm uppercase tracking-wider transition-all ${
                  isPlaying 
                    ? 'bg-orange-500/20 text-orange-400 border border-orange-500/50' 
                    : 'bg-green-500/20 text-green-400 border border-green-500/50 hover:bg-green-500/30'
                } ${simulationPhase === 'neutralized' ? 'opacity-50 cursor-not-allowed' : ''}`}
                data-testid="play-pause-btn"
              >
                {isPlaying ? <Pause size={18} /> : <Play size={18} />}
                {isPlaying ? 'Pause' : 'Play'}
              </button>
              <button
                onClick={resetSimulation}
                className="flex items-center gap-2 px-4 py-2 rounded font-mono text-sm uppercase tracking-wider bg-zinc-700/50 text-zinc-300 border border-zinc-600 hover:bg-zinc-700 transition-all"
                data-testid="reset-btn"
              >
                <RotateCcw size={18} />
                Reset
              </button>
            </div>
            <div className={`px-3 py-1 rounded font-mono text-xs uppercase tracking-wider ${
              simulationPhase === 'ready' ? 'bg-zinc-700 text-zinc-300' :
              simulationPhase === 'detecting' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/50' :
              simulationPhase === 'engaging' ? 'bg-red-500/20 text-red-400 border border-red-500/50 animate-pulse' :
              'bg-green-500/20 text-green-400 border border-green-500/50'
            }`}>
              {simulationPhase === 'ready' && 'Ready'}
              {simulationPhase === 'detecting' && 'Detecting'}
              {simulationPhase === 'engaging' && 'Engaging'}
              {simulationPhase === 'neutralized' && 'Secured'}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex">
        {/* 3D View */}
        <div className="flex-1 relative">
          <div ref={containerRef} className="w-full h-full" />

          {/* Defense Layers Panel */}
          <div className="absolute bottom-4 left-4 bg-black/80 backdrop-blur-md border border-zinc-800 p-4 rounded-sm max-w-xs">
            <h3 className="text-sm font-bold uppercase tracking-wider mb-3 text-sky-500 flex items-center gap-2">
              <Shield size={16} />
              Defense Layers
            </h3>
            <div className="space-y-2 text-xs">
              <div className="flex items-center justify-between">
                <span className="text-zinc-400">Long Range:</span>
                <span className="text-sky-400 font-mono">5km</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-zinc-400">Medium Range:</span>
                <span className="text-cyan-400 font-mono">2km</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-zinc-400">Close-In:</span>
                <span className="text-teal-400 font-mono">1km</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel */}
        <div className="w-80 bg-zinc-900/50 border-l border-zinc-800 flex flex-col">
          {/* Threat Status */}
          <div className="p-4 border-b border-zinc-800">
            <h3 className="text-sm font-bold uppercase tracking-wider mb-3 flex items-center gap-2">
              <AlertTriangle size={16} className="text-red-500" />
              Threat Status
            </h3>
            <div className="space-y-2">
              {[
                { id: 'air1', label: 'Air Threat 1', color: 'bg-red-500', shape: 'rotate-45' },
                { id: 'air2', label: 'Air Threat 2', color: 'bg-orange-500', shape: 'rotate-45' },
                { id: 'surface', label: 'Surface Threat', color: 'bg-green-500', shape: '' },
                { id: 'underwater', label: 'Underwater Threat', color: 'bg-blue-500', shape: 'rounded-full' }
              ].map(t => (
                <div key={t.id} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 ${t.color} ${t.shape}`}></div>
                    <span className="text-zinc-300">{t.label}</span>
                  </div>
                  <span className={`font-mono uppercase ${getStatusColor(threatStatus[t.id])}`}>
                    {threatStatus[t.id]}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Event Log */}
          <div className="flex-1 p-4 flex flex-col min-h-0">
            <h3 className="text-sm font-bold uppercase tracking-wider mb-3">Event Log</h3>
            <div className="flex-1 overflow-y-auto space-y-1 font-mono text-xs bg-black/30 rounded p-2">
              {logs.length === 0 ? (
                <p className="text-zinc-500">Press PLAY to start simulation...</p>
              ) : (
                logs.map((log, i) => (
                  <div key={i} className={getLogColor(log.type)}>
                    <span className="text-zinc-600">[{log.timestamp}]</span> {log.message}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Legend */}
          <div className="p-4 border-t border-zinc-800">
            <h3 className="text-sm font-bold uppercase tracking-wider mb-2">Legend</h3>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-red-500/50 border border-red-500 rotate-45"></div>
                <span className="text-zinc-400">Air (High)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-orange-500/50 border border-orange-500 rotate-45"></div>
                <span className="text-zinc-400">Air (Med)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-500/50 border border-green-500"></div>
                <span className="text-zinc-400">Surface</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-blue-500/50 border border-blue-500 rounded-full"></div>
                <span className="text-zinc-400">Underwater</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
